import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isPromotix } from '../utils/agentConfig';
import { getSessionStorageWithExpiry } from '../utils/storage';

/**
 * Ordered step routes. Must match Layout.js pathToIndex and sidebar.
 * Access to a step or /Completed is allowed only when all previous steps are completed (submitted).
 */
const STEP_ORDER = [
  { path: '/ClientInfo', key: 'clientInfo' },
  { path: '/CorporateInfo', key: 'corporateInfo' },
  { path: '/ContactInfo', key: 'contactInfo' },
  { path: '/BusinessInfo', key: 'businessInfo' },
  { path: '/VolumeInfo', key: 'volumeInfo' },
  { path: '/BankInfo', key: 'bankInfo' },
  { path: '/PricingInfo', key: 'pricingInfo' },
  { path: '/TermsAndConditions', key: 'termsAndConditions' },
  { path: '/eSignature', key: 'eSignature' },
];

// Promotix runs Pricing right after Client (between Client and Corporate). All other
// agents use STEP_ORDER above unchanged.
const PROMOTIX_STEP_ORDER = [
  { path: '/ClientInfo', key: 'clientInfo' },
  { path: '/PricingInfo', key: 'pricingInfo' },
  { path: '/CorporateInfo', key: 'corporateInfo' },
  { path: '/ContactInfo', key: 'contactInfo' },
  { path: '/BusinessInfo', key: 'businessInfo' },
  { path: '/VolumeInfo', key: 'volumeInfo' },
  { path: '/BankInfo', key: 'bankInfo' },
  { path: '/TermsAndConditions', key: 'termsAndConditions' },
  { path: '/eSignature', key: 'eSignature' },
];

const CLIENT_INFO_PATH = '/ClientInfo';

function getCompletedSections() {
  try {
    return JSON.parse(sessionStorage.getItem('completedSections')) || {};
  } catch {
    return {};
  }
}

/**
 * Call when the user successfully completes a step (submits form and moves to next).
 * Marks the step complete in sessionStorage so sidebar and StepGuard allow progress.
 */
export function markStepComplete(stepKey) {
  const completed = getCompletedSections();
  completed[stepKey] = true;
  sessionStorage.setItem('completedSections', JSON.stringify(completed));
}

/**
 * Protects step routes and /Completed: if the user has not completed all previous steps,
 * redirects to Client Info. Uses the same completedSections as the sidebar (grey/green).
 */
const StepGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const completedSections = getCompletedSections();
  const currentPath = location.pathname;

  // Promotix uses a reordered flow (Pricing after Client); every other agent uses STEP_ORDER.
  const agentId = getSessionStorageWithExpiry('agentId');
  const stepOrder = isPromotix(agentId) ? PROMOTIX_STEP_ORDER : STEP_ORDER;

  // /Completed is allowed only when all steps are completed (eSignature done)
  const isCompletedPage = currentPath === '/Completed';
  const currentIndex = isCompletedPage
    ? stepOrder.length
    : stepOrder.findIndex((s) => s.path === currentPath);

  const firstIncompleteIndex = stepOrder.findIndex((s) => !completedSections[s.key]);
  const shouldRedirect =
    currentIndex !== -1 &&
    firstIncompleteIndex !== -1 &&
    currentIndex > firstIncompleteIndex;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(CLIENT_INFO_PATH, { replace: true });
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }
  return <>{children}</>;
};

export default StepGuard;
